/**
 * Thin wrappers that inject the web app's `db`/`drizzle` into the
 * organization layer's local-first queries (org_settings/campus/room). Reactive
 * via `createPowerSyncQuery`/`Single` (auto-invalidate when the table changes).
 */

import type { Accessor } from "solid-js";
import {
  academyOrgQueryKeys,
  getCampusesQuery,
  getGradingModelsQuery,
  getOrgSettingsQuery,
  getRoomsByCampusQuery,
  gradingModelsQueryKeys,
} from "@space/shared";
import { createPowerSyncQuery, createPowerSyncSingle } from "@space/shared/solid";
import type { Campus, GradingModel, OrgSettings, Room } from "@space/powersync";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useDrizzle } from "@/hooks/use-drizzle";
import { OFFLINE_CACHE_STALE_TIME_MS } from "@/lib/constants";

function useBase() {
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  return { db, drizzle, staleTime: OFFLINE_CACHE_STALE_TIME_MS };
}

/** An organization's (singleton) settings. */
export function useOrgSettings(orgId: Accessor<string | undefined>) {
  const base = useBase();
  return createPowerSyncSingle<OrgSettings>({
    queryKey: () => academyOrgQueryKeys.settings(orgId()),
    db: base.db,
    queryFn: () => {
      const db = base.drizzle();
      const id = orgId();
      return db && id ? getOrgSettingsQuery(db, id) : null;
    },
    enabled: () => Boolean(orgId()) && Boolean(base.drizzle()),
    staleTime: base.staleTime,
    suspense: false,
  });
}

/** An organization's campuses. */
export function useCampuses(orgId: Accessor<string | undefined>) {
  const base = useBase();
  return createPowerSyncQuery<Campus>({
    queryKey: () => academyOrgQueryKeys.campuses(orgId()),
    db: base.db,
    queryFn: () => {
      const db = base.drizzle();
      const id = orgId();
      return db && id ? getCampusesQuery(db, id) : null;
    },
    enabled: () => Boolean(orgId()) && Boolean(base.drizzle()),
    staleTime: base.staleTime,
    suspense: false,
  });
}

/** A campus's rooms. */
export function useRooms(campusId: Accessor<string | undefined>) {
  const base = useBase();
  return createPowerSyncQuery<Room>({
    queryKey: () => academyOrgQueryKeys.rooms(campusId()),
    db: base.db,
    queryFn: () => {
      const db = base.drizzle();
      const id = campusId();
      return db && id ? getRoomsByCampusQuery(db, id) : null;
    },
    enabled: () => Boolean(campusId()) && Boolean(base.drizzle()),
    staleTime: base.staleTime,
    suspense: false,
  });
}

/** An organization's grading models (system + custom). */
export function useGradingModels(orgId: Accessor<string | undefined>) {
  const base = useBase();
  return createPowerSyncQuery<GradingModel>({
    queryKey: () => gradingModelsQueryKeys.byOrg(orgId()),
    db: base.db,
    queryFn: () => {
      const db = base.drizzle();
      const id = orgId();
      return db && id ? getGradingModelsQuery(db, id) : null;
    },
    enabled: () => Boolean(orgId()) && Boolean(base.drizzle()),
    staleTime: base.staleTime,
    suspense: false,
  });
}

/** Write access (drizzle) to the organization layer. */
export function useAcademyWrite() {
  const drizzle = useDrizzle();
  return { drizzle };
}
